import {
  type Component,
  For,
  Show,
  createResource,
  createSignal,
  onCleanup,
} from "solid-js";

import { listAttachments } from "@/api/attachments";
import type { AttachmentSummary } from "@/api/generated";

import type { AttachmentsStore } from "./attachmentsStore";

export interface AttachmentsLibraryProps {
  store: AttachmentsStore;
  /** True while a stream is in flight — disables the trigger. */
  disabled?: boolean;
  /**
   * Receives a callback the parent can invoke to open the popover
   * imperatively (e.g. from a slash-menu item). Called once on mount.
   */
  triggerRef?: (open: () => void) => void;
  /** Hide the visible folder button; the popover still works via triggerRef. */
  chipHidden?: boolean;
  /**
   * Invoked when the user clicks the "upload new" action inside the
   * popover. The parent wires this to AttachmentPicker's trigger.
   */
  onUpload?: () => void;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / (1024 * 1024)).toFixed(1)}MB`;
}

const AttachmentsLibrary: Component<AttachmentsLibraryProps> = (props) => {
  const [open, setOpen] = createSignal(false);
  let popoverEl: HTMLDivElement | undefined;

  // Expose an imperative open() so the slash menu can invoke us
  // without rendering the legacy folder button.
  props.triggerRef?.(() => setOpen(true));

  // Lazy-fetch: source = open() so it only hits the API on first open.
  // Each subsequent open re-uses the cached list; user can call refetch
  // manually if needed (not exposed yet — fresh-open is rare enough).
  const [attachments] = createResource(open, () => listAttachments(), {
    initialValue: { attachments: [] },
  });

  const close = () => setOpen(false);

  // Click-outside dismiss. Capture-phase listener so it runs before the
  // trigger button's onClick (which would re-toggle).
  const onDocClick = (e: MouseEvent) => {
    if (!open()) return;
    if (popoverEl && !popoverEl.contains(e.target as Node)) {
      close();
    }
  };
  document.addEventListener("click", onDocClick, true);
  onCleanup(() => document.removeEventListener("click", onDocClick, true));

  const reuse = (att: AttachmentSummary) => {
    // Skip if already pinned to the composer.
    if (props.store.list().some((a) => a.id === att.id)) return;
    props.store.add({
      id: att.id,
      filename: att.filename,
      mime: att.mime,
      bytes: Number(att.bytes),
      status: "ready",
    });
  };

  const isAlreadyPinned = (id: string) =>
    props.store.list().some((a) => a.id === id);

  return (
    <div class="chat-attachments-library" ref={popoverEl}>
      <Show when={!props.chipHidden}>
        <button
          type="button"
          class="chat-attachment-library-btn"
          aria-label="prior uploads"
          title="prior uploads"
          onClick={() => setOpen((v) => !v)}
          disabled={props.disabled}
        >
          📁
        </button>
      </Show>
      <Show when={open()}>
        <div class="chat-attachments-popover" role="menu">
          <header>
            <b>your uploads</b>
            <Show when={props.onUpload}>
              <button
                type="button"
                class="neuro-action subtle"
                onClick={() => {
                  props.onUpload?.();
                  close();
                }}
              >
                upload new
              </button>
            </Show>
            <button type="button" onClick={close} aria-label="close">
              ×
            </button>
          </header>
          <Show
            when={!attachments.loading}
            fallback={<p class="chat-context-loading">loading…</p>}
          >
            <Show
              when={(attachments()?.attachments ?? []).length > 0}
              fallback={
                <p class="chat-attachments-empty">
                  no uploads yet — use "upload new" above
                </p>
              }
            >
              <ul>
                <For each={attachments()?.attachments ?? []}>
                  {(a) => (
                    <li>
                      <button
                        type="button"
                        onClick={() => {
                          reuse(a);
                          close();
                        }}
                        disabled={isAlreadyPinned(a.id) || a.status !== "ready"}
                        title={
                          isAlreadyPinned(a.id)
                            ? "already attached"
                            : a.status !== "ready"
                              ? `status: ${a.status}`
                              : "attach to this turn"
                        }
                      >
                        <span class="chat-attachments-name">{a.filename}</span>
                        <small>
                          {formatBytes(Number(a.bytes))} ·{" "}
                          {a.chunk_count > 0
                            ? `${a.chunk_count} chunks`
                            : a.inline
                              ? "inline"
                              : a.status}
                        </small>
                      </button>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </Show>
        </div>
      </Show>
    </div>
  );
};

export default AttachmentsLibrary;
